import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { CountryStore } from '@indutrust/shared-data';
import type {
  Contact,
  ContactForm as ContactFormModel,
  RequestStatus,
} from '@indutrust/shared-model';
import {
  CheckBoxComponent,
  CountrySelectComponent,
  InputComponent,
} from '@indutrust/shared-ui';
import {
  contactFormSchema,
  createContactFormValue,
  getDialCodeByCountryId,
  provideTranslocoScopeSharedI18n,
  serializeContactForm,
} from '@indutrust/shared-util';
import { TranslocoDirective } from '@jsverse/transloco';

/**
 * Reactive form component used to create or edit a {@link Contact}.
 *
 * Integrates country selection via {@link CountryStore} and validates phone numbers
 * with a country-specific format. Emits validity changes and a serialized contact payload
 * on submit.
 *
 * @example
 * <idt-contact-form (submitted)="save($event)" (validChange)="onValid($event)" />
 */
@Component({
  selector: 'idt-contact-form',
  imports: [
    CheckBoxComponent,
    CountrySelectComponent,
    FormField,
    InputComponent,
    TranslocoDirective,
  ],
  template: `
    <form
      *transloco="let t; prefix: 'sharedContact.form'"
      class="flex flex-col gap-4"
      (ngSubmit)="submitForm()">
      <idt-input
        prefixIcon="user"
        [errorMessage]="t('first_name.errorMessage.notValid')"
        [formField]="contactForm.first_name"
        [label]="t('first_name.label')" />
      <idt-input
        prefixIcon="user"
        [errorMessage]="t('last_name.errorMessage.notValid')"
        [formField]="contactForm.last_name"
        [label]="t('last_name.label')" />
      <idt-input
        prefixIcon="mail"
        type="email"
        [errorMessage]="t('email.errorMessage.notValid')"
        [formField]="contactForm.email"
        [label]="t('email.label')" />

      <idt-country-select
        appendTo="body"
        [countries]="countryStore.entitiesOrdered()"
        [errorMessage]="t('country_id.errorMessage.notValid')"
        [formField]="contactForm.country_id" />
      <idt-input
        prefixIcon="phone"
        [addOnBefore]="phoneDialCode()"
        [errorMessage]="
          t('number.errorMessage.notValid', {
            expectedFormat: phoneExpectedFormat(),
          })
        "
        [formField]="contactForm.number"
        [label]="t('number.label')" />
      <idt-check-box
        [formField]="contactForm.has_whatsapp"
        [label]="t('has_whatsapp.label')" />
    </form>
  `,
  styles: `
    :host {
      display: flex;
      flex: 1;
    }
  `,
  providers: [provideTranslocoScopeSharedI18n()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactFormComponent {
  protected readonly countryStore = inject(CountryStore);

  readonly contact = input<Contact>();
  readonly loading = input(false);
  readonly requestStatus = input<RequestStatus>();

  readonly submitted = output<Omit<Contact, 'id'>>();
  readonly validChange = output<boolean>();

  private readonly contactModel = signal<ContactFormModel>(
    createContactFormValue()
  );

  readonly phoneDialCode = computed(() =>
    getDialCodeByCountryId(Number(this.contactModel().country_id))
  );

  readonly contactForm = form(this.contactModel, contactFormSchema);

  /**
   * Surfaces the expected phone format coming from the cross-field
   * `phoneNumberValidator` so the translation interpolation in the error
   * message stays accurate while the user updates the country.
   */
  readonly phoneExpectedFormat = computed(() => {
    const phoneError = this.contactForm.number().getError('invalidPhoneNumber');
    if (!phoneError || !('expectedFormat' in phoneError)) {
      return undefined;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- narrowed by getError('invalidPhoneNumber') + `in` check
    return (phoneError as { expectedFormat?: string }).expectedFormat;
  });

  protected readonly emitValidChange = effect(() => {
    this.validChange.emit(this.contactForm().valid());
  });
  protected readonly resetFormOnRequestSuccess = effect(() => {
    if (this.requestStatus() === 'success') {
      this.contactModel.set(createContactFormValue());
      this.contactForm().reset();
    }
  });
  protected readonly updateFormOnContact = effect(() => {
    const incoming = this.contact();
    if (incoming) {
      this.contactModel.set(createContactFormValue(incoming));
    }
  });

  /**
   * Submits the form when valid and emits the serialized payload.
   */
  submitForm() {
    if (this.contactForm().valid()) {
      this.submitted.emit(serializeContactForm(this.contactModel()));
    }
  }
}
